import type { ListingStatus } from '../views/shared/listing-detail-pane.js';
import { statusAlter } from './status-watch.js';

// ============================================================================
// Regeln fuer den Push-Button in der Tab-Leiste des geteilten Detail-Panes
// (apps/frontend-vario-kaufland/src/views/shared/listing-detail-pane.ts).
// Bewusst DB- und DOM-frei, damit die Regeln testbar sind - siehe
// publish-button.test.ts.
//
// WICHTIG zur Beschriftung: "Listen" und "Aufschalten" sind im Lister ZWEI
// verschiedene Absichten, die der Haendler im Vario-Slot selbst waehlt
// (listing-status.ts, Kopfkommentar: Pfad A "Aufschalten" per EAN-Suche,
// Pfad B "Neu listen" per Kategorie-Vorschlag). Der Button traegt deshalb den
// Namen des GEWAEHLTEN Modus und nicht einen festen. Frueher hiess er immer
// "Listen" und schaltete auf - also der Name des einen Modus auf der Aktion
// des anderen.
// ============================================================================

/** Beschriftung des Push-Buttons. Entspricht den beiden Modus-Namen aus dem
 *  Slot und dem Modus-Filter im Lister. */
export const LABEL_ATTACH = 'Aufschalten';
export const LABEL_NEW = 'Listen';
/** Beschriftung, sobald das Angebot bei Kaufland steht. Dann heisst der Knopf nicht mehr
 *  "Listen" -- es gibt nichts mehr zu listen, sondern etwas nachzureichen. */
export const LABEL_RESEND = 'Erneut senden';

/**
 * Normalisiert den rohen `mode`-Wert aus `kaufland.listings` auf die drei Werte, die
 * `publishButtonState` kennt.
 *
 * Alles, was weder `'attach'` noch `'new'` ist, wird `'unknown'` und damit gesperrt, statt
 * als `'new'` durchzugehen. Der Grund hat sich mit diesem Branch gedreht: Solange `'new'`
 * hart gesperrt war, war es der fail-safe Rueckfall. Jetzt reicht `'new'` Produktdaten in
 * Kauflands Katalog ein, und dafuer gibt es kein DELETE. Ein null-Wert, ein leerer String
 * oder ein kuenftiger dritter Modus darf diese Aktion nicht ausloesen.
 */
/** Was die Statuszeile der Kategorie-Leiste sagt. */
export interface StatusSatzEingabe {
  status: string;
  /** Kauflands letzte Rueckmeldung zur laufenden Pruefung. */
  reason?: string | null | undefined;
  /** Letzter Fehler beim Aufschalten. */
  lastError?: string | null | undefined;
  /** Wie lange die Pruefung schon laeuft (seit `product_data_submitted_at`), in ms. */
  pruefdauerMs?: number | null | undefined;
  /** Alter des angezeigten Standes (seit `kaufland_status_polled_at`), in ms. */
  standAlterMs?: number | null | undefined;
}

/**
 * Der erklaerende Satz zum Listing-Status.
 *
 * Uebernommen aus dem Vario-Slot (`listing-status.ts`, `statusLineText`), als die Box
 * „Aktuell zugeordnet" am 2026-08-17 in die Kategorie-Leiste des Detail-Fensters
 * gewandert ist. Der Wortlaut ist bewusst derselbe -- zwei Formulierungen fuer denselben
 * Zustand waeren fuer den Haendler zwei verschiedene Zustaende.
 *
 * Kauflands Wartegrund wird ANGEHAENGT, nicht uebersetzt: Der Wert ist ein technischer
 * Schluessel (`NOT_YET_PROCESSED`), die Liste moeglicher Werte ist nicht gemessen. Eine
 * Uebersetzungstabelle waere geraten -- roh und ehrlich schlaegt huebsch und falsch.
 *
 * Pure -- keine IO.
 */
/** " seit 10 Min." — leer, solange die Dauer unbekannt oder noch unter einer Minute liegt.
 *  Eine Dauer von "0 Min." wäre Lärm; die Aussage ist erst ab einer Minute interessant. */
function pruefdauerSatz(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 60_000) return '';
  return ` seit ${Math.floor(ms / 60_000)} Min.`;
}

export function statusSatz(d: StatusSatzEingabe): string | null {
  switch (d.status) {
    case 'queued':
      return 'Wird gelistet, warte auf Status.';
    case 'validating': {
      // "Letzte Rückmeldung: fehlende Attribute: Beschreibung" las sich wie ein Urteil über
      // UNSERE Daten -- der Betreiber schloss daraus am 2026-08-20, das Mapping sei kaputt.
      // Es ist aber ein ZWISCHENSTAND: Kaufland sagt, was im Katalogeintrag in diesem Moment
      // noch fehlt, und `PUT /v2/product-data/` ergänzt asynchron. Im gemessenen Fall war die
      // Beschreibung längst eingereicht (1840 Zeichen) und das Listing 31 Minuten später
      // freigegeben. Der Satz nennt deshalb die Dauer und das Alter des Standes.
      const seit = pruefdauerSatz(d.pruefdauerMs);
      if (!d.reason) return `Kaufland prüft die Produktdaten${seit}, warte auf Freigabe.`;
      const stand = statusAlter(d.standAlterMs ?? null);
      const standSatz = stand ? ` (Stand ${stand})` : '';
      return `Kaufland prüft die Produktdaten${seit}. Zwischenstand${standSatz}: ${d.reason}.`;
    }
    case 'error':
      return d.lastError ?? 'Beim Aufschalten ist ein Fehler aufgetreten.';
    case 'published':
      return 'Das Angebot ist bei Kaufland aufgeschaltet.';
    case 'mapped':
      return 'Bereit zum Aufschalten.';
    default:
      // 'pending', 'archived' und alles Unbekannte: Die Pille daneben sagt bereits genug,
      // ein Satz ohne Mehrwert ist nur Laerm.
      return null;
  }
}

export function normalizeListingMode(raw: unknown): 'new' | 'attach' | 'undecided' | 'unknown' {
  if (raw === 'attach') return 'attach';
  if (raw === 'new') return 'new';
  if (raw === 'undecided') return 'undecided';
  return 'unknown';
}

export interface PublishButtonState {
  enabled: boolean;
  title: string;
  /** Was auf dem Button steht. Folgt dem Modus des Listings, nicht der Aktion. */
  label: string;
}

/**
 * Entscheidet Beschriftung und Klickbarkeit des Push-Buttons.
 *
 * Die verbleibenden Sperren sind nicht kosmetisch, sondern spiegeln den Worker
 * bzw. einen eigenen Fehlerfall:
 *   * mode='unknown' heisst "der Mode-Lookup ist fehlgeschlagen" - ein transienter
 *     Fehler (PostgREST, abgelaufenes JWT, RLS), keine Produkt-Grenze. Deshalb ein
 *     eigener Tooltip, sonst haelt der User einen Server-Fehler faelschlich fuer
 *     eine dauerhafte Einschraenkung und versucht es nie erneut.
 *   * mode='undecided' heisst "der Haendler hat sich noch nicht entschieden" - kein
 *     Fehler, sondern ein regulaerer Zustand mit eigenem Wortlaut. Der Rat "bitte
 *     erneut versuchen" waere hier nutzlos: Erneutes Versuchen aendert nichts, weil
 *     nichts kaputt ist.
 *   * fehlender (undefined) mode heisst "noch nie hydriert" - dann ebenfalls
 *     nicht auf attach raten.
 *   * status='validating' heisst: das Listing wurde bereits eingereicht, Kaufland
 *     prueft die Stammdaten. Ein zweiter Klick wuerde nur eine weitere Einreichung
 *     ausloesen, deshalb gesperrt, unabhaengig vom Modus.
 *
 * mode='new' war frueher hart gesperrt (Phase 1: der Worker lehnte die
 * Produktanlage ab). Der Worker kann sie jetzt, deshalb faellt mode='new'
 * genau wie mode='attach' in den Status-basierten switch unten - nur die
 * Beschriftung unterscheidet sich weiterhin nach Modus.
 *
 * Ohne bekannten Modus traegt der Button LABEL_ATTACH, weil Aufschalten die
 * einzige Aktion ist, die er ueberhaupt ausfuehren kann. Er ist in diesen
 * Faellen ohnehin gesperrt.
 */
export function publishButtonState(
  row: { status: ListingStatus; mode?: 'new' | 'attach' | 'undecided' | 'unknown' } | null | undefined,
): PublishButtonState {
  if (!row) {
    return { enabled: false, label: LABEL_ATTACH, title: 'Zuerst ein Listing auswählen' };
  }
  if (row.mode === 'undecided') {
    return {
      enabled: false,
      label: LABEL_ATTACH,
      title: 'Noch nicht entschieden, ob aufgeschaltet oder neu gelistet wird — bitte oben waehlen',
    };
  }
  if (row.mode === 'unknown') {
    return {
      enabled: false,
      label: LABEL_ATTACH,
      title: 'Modus konnte nicht ermittelt werden, bitte erneut versuchen',
    };
  }
  if (row.mode !== 'attach' && row.mode !== 'new') {
    return {
      enabled: false,
      label: LABEL_ATTACH,
      title: 'Modus konnte nicht ermittelt werden, bitte erneut versuchen',
    };
  }
  const label = row.mode === 'new' ? LABEL_NEW : LABEL_ATTACH;
  switch (row.status) {
    case 'queued':
      return { enabled: false, label, title: 'Aufschaltung läuft bereits' };
    case 'validating':
      // Frueher gesperrt, seit dem 2026-08-07 frei. Die Sperre stand auf der Annahme, dass
      // waehrend einer Pruefung nur warten sinnvoll ist. Die erste echte Einreichung hat sie
      // widerlegt: Kaufland antwortete `MISSING_ATTRIBUTES: ["Hersteller","Bild"]` -- ein
      // Zustand, den KEIN Warten aufloest, denn Kaufland liest unsere Daten nicht von selbst
      // noch einmal. Der Haendler korrigiert, und genau dann braucht er diesen Knopf. Mit der
      // Sperre lief das Listing stattdessen in die 24-Stunden-Grenze.
      //
      // Der Enqueue-RPC bricht dafuer den offenen Poll-Job ab und reicht wirklich neu ein
      // (Migration 0032); ein zweites PUT auf dieselbe EAN ist bei product-data kein
      // Duplikat, sondern der vorgesehene Aenderungsweg.
      return { enabled: true, label, title: 'Produktdaten erneut bei Kaufland einreichen' };
    case 'published':
      // Frueher gesperrt ("Angebot ist bereits aufgeschaltet"), seit dem 2026-08-08 frei.
      //
      // Die Sperre stand auf der Annahme, nach dem Aufschalten gebe es nichts mehr zu tun.
      // Das stimmt nicht: Kaufland bewertet die Stammdaten weiter und ruegt Attribute -- am
      // 2026-08-08 das Gewicht -- und Vario-Stammdaten aendern sich ohnehin. Der Fix fuer das
      // Gewicht war ausgerollt und erreichte Kaufland trotzdem nie, weil dieser Knopf zu war.
      //
      // Der Enqueue-RPC setzt dafuer product_data_approved_at mit zurueck (Migration 0034),
      // sonst spraenge der Job direkt zur Angebots-Anlage und schickte nie eine
      // Aktualisierung der Stammdaten.
      return { enabled: true, label: LABEL_RESEND, title: 'Stammdaten und Angebot erneut an Kaufland senden' };
    case 'archived':
      return { enabled: false, label, title: 'Listing ist archiviert' };
    default:
      // pending | mapping | mapped | error
      return { enabled: true, label, title: 'Angebot bei Kaufland aufschalten' };
  }
}

// publishBlockReason() stand hier bis zum 2026-08-07 und ist mit dem Abschnitt
// „Deine Verkaufsbedingungen" entfallen.
//
// Sie pruefte die gespeicherten offerOverrides, bevor der Kopfleisten-Knopf einen
// Push einreihte. Dieser Knopf reiht jetzt nicht mehr blind ein, sondern nimmt die
// Werte aus dem Formular, speichert sie und reiht dann ein. Geprueft wird deshalb,
// was WIRKLICH rausgeht, und zwar in parseOfferOverrides()
// (views/shared/offer-conditions-form.ts) mit denselben Regeln.
//
// Ihr dritter Parameter `formValues` war ohnehin nie erreichbar: er las
// `#klst-attach-form`, das nur im attach-Panel existiert, und dort gibt es keine
// Kopfleiste und damit keinen Aufrufer. Details im Bericht vom 2026-08-07.
